import { createClient } from '@supabase/supabase-js';
import { toast } from 'react-toastify';

// Get Supabase configuration from environment variables
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY;

// Create a single supabase client for interacting with your database
export const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

// Helper function to handle errors with toast notifications
export const handleError = (error, customMessage = 'An error occurred') => {
  console.error(`Supabase error: ${customMessage}`, error);
  
  // Show toast notification
  toast.error(customMessage);
  
  return { error: true, message: customMessage };
};

// Authentication helpers
export const getCurrentUser = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  return user;
};

export const isAuthenticated = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
};

// Social login with Google
export const signInWithGoogle = async () => {
  try {
    // Using default redirect to current URL
    const { data, error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: 'https://mealmaestro.netlify.app//auth/callback'

      }
    });
    
    if (error) throw error;
    
    // Return data that includes the URL to redirect to
    return { data, error: null };
  } catch (error) {
    console.error('Google sign-in error:', error);
    toast.error('Failed to sign in with Google. Please try again.');
    return { data: null, error };
  }
};

// Handle auth state changes - useful for components that need to react to login/logout
export const authStateListener = (callback) => {
  const { data: { subscription } } = supabase.auth.onAuthStateChange(
    (event, session) => {
      // When a user signs in with OAuth, create or update their profile
      if (event === 'SIGNED_IN' && session?.user) {
        createOrUpdateUserProfile(session.user);
      }
      callback(session?.user || null);
    }
  );
  
  return subscription;
};

// Helper to create or update user profile after OAuth login
const createOrUpdateUserProfile = async (user) => {
  if (!user) return;
  
  try {
    // Check if profile exists
    const { data: existingProfile } = await supabase
      .from('profiles')
      .select('*')
      .eq('id', user.id)
      .single();
    
    if (existingProfile) {
      // Update existing profile with latest info from provider
      await supabase
        .from('profiles')
        .update({
          name: user.user_metadata?.name || user.user_metadata?.full_name,
          avatar_url: user.user_metadata?.avatar_url,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);
    } else {
      // Create new profile for first-time OAuth users
      await supabase
        .from('profiles')
        .insert([{
          id: user.id,
          name: user.user_metadata?.name || user.user_metadata?.full_name,
          email: user.email,
          avatar_url: user.user_metadata?.avatar_url,
          preferences: {
            dietary: [],
            allergies: [],
            favorite_ingredients: []
          }
        }]);
    }
  } catch (error) {
    console.error('Error updating user profile after OAuth:', error);
  }
};
